import { Button, Dialog } from "../components";
import { ControlSelect, TypeSelect, ValidatedInput } from "./components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addControl,
  deleteControl,
  getControls,
  getDeviceVoiceData,
  getError,
  getInProgressState,
  initEdit,
  selectDevice,
} from "../redux/voiceControl-slice";
import {
  deviceSettings,
  deviceSettingsError,
  form,
} from "./DeviceSettings.module.scss";
import { getControlSectionId, getControlZoneId } from "../utils";
import {
  getDevice,
  getUserDeviceState,
  initDeviceDetails,
} from "../redux/devices-slice";
import {
  getErrorMsg,
  isValid,
  validateForm,
} from "../services/validation-service";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "../components";
import Skeleton from "react-loading-skeleton";
import cn from "classnames";
import { label } from "../components/Select.module.scss";
import { scrollToFirstValidationError } from "../utils";
import sharedStyles from "../shared.module.scss";
import styles from "./VoiceControlEditPage.module.scss";
import { useFormatMessage } from "../i18n";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom";
import { voiceControlValidation } from "../shared/validation";

const errorMessages = {
  BAD_REQUEST: "error_name_or_type",
  VOICE_INVALID_DEVICE: "error_voice_invalid_device",
  NOT_FOUND: "error_device_not_found",
  VOICE_INVALID_STATE: "error_voice_invalid_state",
  SERVER_ERROR: "something_went_wrong",
};

export function VoiceControlEditPage() {
  const { cloudId, controlId } = useParams();
  const inProgress = useSelector(getInProgressState);
  const error = useSelector(getError);
  const history = useHistory();
  const tr = useFormatMessage();
  const dispatch = useDispatch();

  const { userDeviceError, userDeviceLoading } =
    useSelector(getUserDeviceState);
  const voiceDevice = useSelector(getDeviceVoiceData(cloudId));
  const [showModal, setShowModal] = useState(false);
  const [pristine, setPristine] = useState(true);

  const currentControl = voiceDevice?.configured.find(
    (control) => control.id === controlId
  );

  const mapControlsWithIds = useCallback((controls) => {
    return controls.map((control) => ({
      ...control,
      zoneId: getControlZoneId(control.id),
      sectionId: getControlSectionId(control.id),
    }));
  }, []);

  const controlsWithIds = useMemo(() => {
    const controls = voiceDevice ? voiceDevice.not_configured : [];
    return mapControlsWithIds(controls);
  }, [mapControlsWithIds, voiceDevice]);

  function controlOptions() {
    if (currentControl) {
      return mapControlsWithIds([currentControl]);
    }
    return controlsWithIds;
  }

  const { setValues, ...formik } = useFormik({
    initialValues: {
      name: currentControl?.friendly_name || currentControl?.name || "",
      type: currentControl ? String(currentControl.type) : "",
      control: JSON.stringify(currentControl) || "",
    },
    onSubmit: async (values) => {
      setPristine(false);
      const { name, type, control } = values;
      if (isValid(validationErrors)) {
        const data = await dispatch(
          addControl({
            cloudId: cloudId,
            control: {
              name: name.trim(),
              type: parseInt(type),
              component: control,
            },
          })
        );
        if (data.meta.requestStatus === "fulfilled") {
          handleSaveClick();
        }
      } else {
        scrollToFirstValidationError();
      }
    },
  });

  useEffect(() => {
    dispatch(initEdit());
    dispatch(initDeviceDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!voiceDevice) {
      dispatch(getDevice(cloudId)).then((data) => {
        if (data.payload.cloudId) {
          dispatch(selectDevice(data.payload));
          dispatch(getControls(cloudId));
        }
      });
    }
  }, [voiceDevice, cloudId, dispatch]);

  useEffect(() => {
    if (currentControl) {
      setValues({
        name: currentControl.friendly_name || currentControl.name,
        type: String(currentControl.type),
        control: JSON.stringify(currentControl),
      });
    }
  }, [currentControl, setValues]);

  function findFirstControlOfType(type) {
    const control = controlsWithIds.find((control) => {
      return control.possible_types.includes(parseInt(type));
    });
    return JSON.stringify(control);
  }

  async function handleDeleteControl() {
    setShowModal(false);
    const data = await dispatch(
      deleteControl({
        cloudId: cloudId,
        controlId: controlId,
      })
    );
    if (data.meta.requestStatus === "fulfilled") {
      handleSaveClick();
    }
  }

  function handleSaveClick() {
    /* This route only exists on mobile, but will be automatically
     * redirected to /voice on desktop (see routes in App.js)
     */
    history.push(`/voice/${cloudId}/controls`);
  }

  function isActionInProgress() {
    return inProgress.addInProgress || inProgress.deleteInProgress;
  }

  const validationErrors = validateForm(
    { form: formik.values },
    voiceControlValidation
  );
  const nameErrorMessage = getErrorMsg(validationErrors, "name");
  const typeErrorMessage = getErrorMsg(validationErrors, "type");

  const errorMessage = tr(errorMessages[voiceDevice?.error || "SERVER_ERROR"]);

  return (
    <div className={cn(sharedStyles.content, deviceSettings)}>
      <div className={cn(sharedStyles.headerContainer, sharedStyles.content)}>
        <h1 className={sharedStyles.pageTitle}>
          {currentControl?.friendly_name || tr("add_control")}
        </h1>
      </div>
      {userDeviceError && (
        <Alert severity="error" className={deviceSettingsError}>
          {tr("fail_getting_device")}
        </Alert>
      )}
      {voiceDevice?.status === "generic_error" && (
        <Alert severity="error" className={deviceSettingsError}>
          {errorMessage}
        </Alert>
      )}
      {controlId && voiceDevice?.loaded && !currentControl && (
        <Alert severity="error" className={deviceSettingsError}>
          {tr("control_not_found")}
        </Alert>
      )}
      <form
        className={cn(form, sharedStyles.form, sharedStyles.formText)}
        onSubmit={formik.handleSubmit}
      >
        <ValidatedInput
          label={tr("name")}
          id="name-input"
          name="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          errors={nameErrorMessage}
          loading={userDeviceLoading || voiceDevice?.loading}
          pristine={pristine}
        />

        <label className={label}>{tr("type")}</label>
        {userDeviceLoading || voiceDevice?.loading ? (
          <Skeleton className={styles.typeSkeleton} />
        ) : (
          <TypeSelect
            controls={controlOptions()}
            formik={formik}
            findFirstControlOfType={findFirstControlOfType}
            disabled={controlId}
            pristine={pristine}
            errors={typeErrorMessage}
          />
        )}

        <label className={label}>{tr("control")}</label>
        <ControlSelect
          formik={formik}
          controls={controlOptions()}
          disabled={controlId || !formik.values.type}
          loading={userDeviceLoading || voiceDevice?.loading}
          deviceSelected={voiceDevice}
        />
        {error && (
          <Alert severity="error" className={deviceSettingsError}>
            <span>{errorMessage}</span>
          </Alert>
        )}

        <div className={cn(sharedStyles.btnGroup, styles.btnGroup)}>
          <Button
            loading={inProgress.addInProgress}
            className={styles.button}
            type="submit"
            title={tr("control_save")}
            disabled={!formik.values.control || isActionInProgress()}
            variant="primary"
          />
          {controlId && (
            <Button
              loading={inProgress.deleteInProgress}
              className={styles.button}
              type="button"
              title={tr("control_delete")}
              disabled={!formik.values.control || isActionInProgress()}
              variant="danger"
              onClick={() => {
                setShowModal(true);
              }}
            />
          )}
        </div>
      </form>
      <Dialog
        content={tr(
          "this_operation_is_not_reversible_are_you_sure_you_want_to_delete_this_control"
        )}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        buttons={[
          {
            title: tr("no_go_back"),
            handler: () => setShowModal(false),
            variant: "light",
          },
          {
            title: tr("yes_i_want_to_proceed"),
            handler: handleDeleteControl,
            variant: "danger",
          },
        ]}
      />
    </div>
  );
}
